import { Linking, Platform } from 'react-native';
import type { WorkOrder, WorkOrderTask, WorkOrderPart } from '../domain/types';
import { formatOtLabel } from './otNumber';
import { WORK_ORDER_STATUS_LABELS } from './workOrderStatus';
import { fallbackSubjectDisplay } from './workOrderSubject';
import { formatMoney } from '../../shared/format/public-api';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Construye el mensaje de WhatsApp con el estado de la OT.
 * Incluye resumen de tareas, repuestos y total estimado.
 */
export function buildWorkOrderWhatsAppMessage(
  wo: WorkOrder,
  tasks: WorkOrderTask[] = [],
  parts: WorkOrderPart[] = [],
  businessName?: string | null,
): string {
  const subject = fallbackSubjectDisplay(wo.subjectType, wo.customerName);
  const greeting = businessName
    ? `Hola ${wo.customerName || ''}, te informamos desde *${businessName}* el estado de tu orden de trabajo:`
    : `Hola ${wo.customerName || ''}, te informamos el estado de tu orden de trabajo:`;
  const lines: string[] = [
    greeting,
    '',
    `*${formatOtLabel(wo.otNumber, wo.id)}*`,
    `Estado: *${WORK_ORDER_STATUS_LABELS[wo.status] ?? wo.status}*`,
    `Asunto: ${subject.short}`,
    `Fecha de ingreso: ${formatDate(wo.createdAt)}`,
  ];

  if (tasks.length > 0) {
    lines.push('', '*Tareas:*');
    for (const t of tasks) {
      lines.push(`• ${t.productName} (${t.quantity}u) — ${formatMoney(t.unitPrice * t.quantity)}`);
    }
  }

  if (parts.length > 0) {
    lines.push('', '*Repuestos:*');
    for (const p of parts) {
      lines.push(`• ${p.productName} (${p.quantity}u) — ${formatMoney(p.unitPrice * p.quantity)}`);
    }
  }

  if (tasks.length > 0 || parts.length > 0) {
    const total = tasks.reduce((s, t) => s + t.unitPrice * t.quantity, 0)
      + parts.reduce((s, p) => s + p.unitPrice * p.quantity, 0);
    lines.push('', `*Total estimado: ${formatMoney(total)}*`);
  }

  if (wo.notes) {
    lines.push(``, `Notas: ${wo.notes}`);
  }

  lines.push('', 'Cualquier consulta, estamos a disposición.');

  return lines.join('\n');
}

/**
 * Abre WhatsApp con un mensaje pre-formateado.
 * Si phone es null/empty, abre WhatsApp sin número (el usuario elige el contacto).
 */
export async function openWhatsAppWithMessage(
  phone: string | null | undefined,
  message: string,
): Promise<void> {
  const encodedMessage = encodeURIComponent(message);
  const cleanPhone = (phone ?? '').replace(/[^0-9]/g, '');
  const url =
    cleanPhone.length > 0
      ? `https://wa.me/${cleanPhone}?text=${encodedMessage}`
      : Platform.OS === 'ios'
        ? `whatsapp://send?text=${encodedMessage}`
        : `https://wa.me/?text=${encodedMessage}`;

  const supported = await Linking.canOpenURL(url);
  if (!supported) {
    throw new Error('WhatsApp no está disponible en este dispositivo.');
  }
  await Linking.openURL(url);
}
